import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API_URL } from '../../Config';
import {
  format,
  parseISO,
  isValid,
  formatDistanceToNow,
  formatDistance
} from 'date-fns';
import { fr } from 'date-fns/locale';

// Icônes
import {
  FiCalendar,
  FiTrash2,
  FiEye,
  FiPackage,
  FiTruck,
  FiUser,
  FiUsers,
  FiSearch,
  FiCheckCircle,
  FiXCircle,
  FiClock,
  FiTruck as FiTruckIcon,
  FiFilter,
  FiDownload,
  FiRefreshCw,
  FiAlertCircle,
  FiChevronDown,
  FiChevronRight,
  FiMail,
  FiX,
} from 'react-icons/fi';
import {
  HiOutlineUserGroup,
  HiOutlineAcademicCap,
  HiOutlineOfficeBuilding,
  HiOutlineDocumentText,
  HiOutlineExclamationCircle,
  HiOutlineCheck,
  HiOutlineX,
  HiOutlineClock
} from 'react-icons/hi';

const EvenementsPage = () => {
  const [evenements, setEvenements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [selectedEvenement, setSelectedEvenement] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedRows, setExpandedRows] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });

  const api = axios.create({
    baseURL: API_URL,
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'application/json'
    }
  });

  useEffect(() => {
    fetchEvenements();
  }, []);

  const fetchEvenements = async () => {
    try {
      setLoading(true);
      const response = await api.get('/evenements');
      setEvenements(response.data);
    } catch (error) {
      showSnackbar('Erreur lors du chargement des événements', 'error');
      console.error('Error fetching evenements:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/evenements/${selectedEvenement.id}`);
      setEvenements(prev => prev.filter(e => e.id !== selectedEvenement.id));
      showSnackbar('Événement supprimé avec succès', 'success');
      setOpenDeleteDialog(false);
      setSelectedEvenement(null);
    } catch (error) {
      const message = error.response?.data?.message || 'Erreur lors de la suppression';
      showSnackbar(message, 'error');
    }
  };

  const handleViewDetails = (evenement) => {
    setSelectedEvenement(evenement);
    setOpenDetailsDialog(true);
  };

  const handleDeleteClick = (evenement) => {
    setSelectedEvenement(evenement);
    setOpenDeleteDialog(true);
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const toggleRowExpansion = (id) => {
    setExpandedRows(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const filteredAndSortedEvenements = useMemo(() => {
    let filtered = evenements.filter(evenement => {
      const matchesTab = activeTab === 'all' || evenement.statut === activeTab;
      
      const matchesSearch = searchTerm === '' || 
        evenement.id.toString().includes(searchTerm) ||
        (evenement.user?.name && evenement.user.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (evenement.beneficiaire?.nom && evenement.beneficiaire.nom.toLowerCase().includes(searchTerm.toLowerCase()));
      
      return matchesTab && matchesSearch;
    });

    filtered.sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    return filtered;
  }, [evenements, activeTab, searchTerm, sortConfig]);

  const getStats = useMemo(() => {
    const stats = {
      total: evenements.length,
      en_attente: evenements.filter(e => e.statut === 'en_attente').length,
      valide: evenements.filter(e => e.statut === 'valide').length,
      refuse: evenements.filter(e => e.statut === 'refuse').length,
      livre: evenements.filter(e => e.statut === 'livre').length
    };
    return stats;
  }, [evenements]);

  const getStatusConfig = (statut) => {
    const configs = {
      valide: {
        icon: <FiCheckCircle className="w-4 h-4" />,
        label: 'Validé',
        color: 'text-emerald-600',
        bg: 'bg-emerald-50',
        border: 'border-emerald-200',
        dot: 'bg-emerald-500'
      },
      refuse: {
        icon: <FiXCircle className="w-4 h-4" />,
        label: 'Refusé',
        color: 'text-red-600',
        bg: 'bg-red-50',
        border: 'border-red-200',
        dot: 'bg-red-500'
      },
      en_attente: {
        icon: <FiClock className="w-4 h-4" />,
        label: 'En attente',
        color: 'text-amber-600',
        bg: 'bg-amber-50',
        border: 'border-amber-200',
        dot: 'bg-amber-500'
      },
      livre: {
        icon: <FiTruck className="w-4 h-4" />,
        label: 'Livré',
        color: 'text-blue-600',
        bg: 'bg-blue-50',
        border: 'border-blue-200',
        dot: 'bg-blue-500'
      }
    };
    return configs[statut] || configs.en_attente;
  };

  const getTypeConfig = (type) => {
    const configs = {
      enterrement: {
        icon: <FiUsers className="w-4 h-4" />,
        label: 'Enterrement',
        color: 'text-gray-700',
        bg: ''
      },
      mariage: {
        icon: <HiOutlineUserGroup className="w-4 h-4" />,
        label: 'Mariage',
        color: 'text-purple-600',
        bg: 'bg-purple-50'
      },
      anniversaire: {
        icon: <FiCalendar className="w-4 h-4" />,
        label: 'Anniversaire',
        color: 'text-pink-600',
        bg: 'bg-pink-50'
      }
    };
    return configs[type] || { icon: <FiCalendar />, label: type, color: 'text-gray-600', bg: '' };
  };

  const getTotalMateriels = (materiels) => {
    if (!materiels) return 0;
    return materiels.reduce((total, materiel) => total + (materiel.pivot?.quantite_demandee || 0), 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = parseISO(dateString);
      if (!isValid(date)) return dateString;
      return format(date, 'dd MMMM yyyy', { locale: fr });
    } catch (error) {
      return dateString;
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = parseISO(dateString);
      if (!isValid(date)) return dateString;
      return format(date, 'dd/MM/yyyy HH:mm', { locale: fr });
    } catch (error) {
      return dateString;
    }
  };

  const getTimeAgo = (dateString) => {
    if (!dateString) return '';
    try {
      const date = parseISO(dateString);
      if (!isValid(date)) return '';
      return formatDistanceToNow(date, { addSuffix: true, locale: fr });
    } catch (error) {
      return '';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen  flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des événements...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen ">
      {/* Header */}
      <div className="border-b border-gray-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <h1 className="text-2xl font-semibold text-gray-900">Gestion des Événements</h1>
              <span className="ml-4 bg-gray-100 text-gray-600 text-sm font-medium px-2.5 py-0.5 rounded-full">
                {evenements.length} événement(s)
              </span>
            </div>
            <button
              onClick={fetchEvenements}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
            >
              <FiRefreshCw className="w-4 h-4" />
              Actualiser
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          <div 
            className={`bg-white rounded-lg border p-6 cursor-pointer transition-all ${activeTab === 'all' ? 'border-blue-500 ring-2 ring-blue-100' : 'border-gray-200 hover:border-gray-300'}`}
            onClick={() => setActiveTab('all')}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total</p>
                <p className="text-3xl font-semibold text-gray-900 mt-2">{getStats.total}</p>
              </div>
              <div className=" p-3 rounded-lg">
                <FiCalendar className="w-6 h-6 text-gray-600" />
              </div>
            </div>
          </div>
          
          <div 
            className={`bg-white rounded-lg border p-6 cursor-pointer transition-all ${activeTab === 'en_attente' ? 'border-amber-500 ring-2 ring-amber-100' : 'border-gray-200 hover:border-gray-300'}`}
            onClick={() => setActiveTab('en_attente')}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">En attente</p>
                <p className="text-3xl font-semibold text-gray-900 mt-2">{getStats.en_attente}</p>
              </div>
              <div className="bg-amber-50 p-3 rounded-lg">
                <FiClock className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </div>
          
          <div 
            className={`bg-white rounded-lg border p-6 cursor-pointer transition-all ${activeTab === 'valide' ? 'border-emerald-500 ring-2 ring-emerald-100' : 'border-gray-200 hover:border-gray-300'}`}
            onClick={() => setActiveTab('valide')}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Validés</p>
                <p className="text-3xl font-semibold text-gray-900 mt-2">{getStats.valide}</p>
              </div>
              <div className="bg-emerald-50 p-3 rounded-lg">
                <FiCheckCircle className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </div>
          
          <div 
            className={`bg-white rounded-lg border p-6 cursor-pointer transition-all ${activeTab === 'refuse' ? 'border-red-500 ring-2 ring-red-100' : 'border-gray-200 hover:border-gray-300'}`}
            onClick={() => setActiveTab('refuse')}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Refusés</p>
                <p className="text-3xl font-semibold text-gray-900 mt-2">{getStats.refuse}</p>
              </div>
              <div className="bg-red-50 p-3 rounded-lg">
                <FiXCircle className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </div>
          
          <div 
            className={`bg-white rounded-lg border p-6 cursor-pointer transition-all ${activeTab === 'livre' ? 'border-blue-500 ring-2 ring-blue-100' : 'border-gray-200 hover:border-gray-300'}`}
            onClick={() => setActiveTab('livre')}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Livrés</p>
                <p className="text-3xl font-semibold text-gray-900 mt-2">{getStats.livre}</p>
              </div>
              <div className="bg-blue-50 p-3 rounded-lg">
                <FiTruck className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Toolbar */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex-1">
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Rechercher par ID, utilisateur ou bénéficiaire..."
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="flex items-center space-x-2">
                <FiFilter className="text-gray-400" />
                <select
                  className="border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={sortConfig.key}
                  onChange={(e) => handleSort(e.target.value)}
                >
                  <option value="created_at">Trier par date</option>
                  <option value="id">Trier par ID</option>
                </select>
                <span className="text-gray-400">
                  {sortConfig.direction === 'asc' ? '↑' : '↓'}
                </span>
              </div>
              
              <button
                onClick={() => {}}
                className="flex items-center gap-2 px-4 py-2.5 border border-gray-300 rounded-lg hover:"
              >
                <FiDownload className="w-4 h-4" />
                <span className="text-sm font-medium">Exporter</span>
              </button>
            </div>
          </div>
        </div>

        {/* Table */}
        {filteredAndSortedEvenements.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
            <FiCalendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Aucun événement trouvé
            </h3>
            <p className="text-gray-500">
              {searchTerm || activeTab !== 'all' 
                ? 'Aucun événement ne correspond à vos critères de recherche'
                : 'Commencez par créer un nouvel événement'}
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Événement
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Utilisateur
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Bénéficiaire
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Statut
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredAndSortedEvenements.map((evenement) => {
                    const statusConfig = getStatusConfig(evenement.statut);
                    const typeConfig = getTypeConfig(evenement.type);
                    const isExpanded = expandedRows[evenement.id];
                    
                    return (
                      <React.Fragment key={evenement.id}>
                        <tr className="hover:">
                          <td className="px-6 py-4">
                            <div className="flex items-center space-x-3">
                              <div className={`${typeConfig.bg} p-2 rounded-lg`}>
                                {typeConfig.icon}
                              </div>
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  #{evenement.id} • {typeConfig.label}
                                </div>
                                <div className="text-xs text-gray-500 mt-1">
                                  {getTotalMateriels(evenement.materiels)} matériel(s) demandé(s)
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            {evenement.user ? (
                              <div className="flex items-center space-x-3">
                                <div className="bg-gray-100 p-2 rounded-lg">
                                  <FiUser className="w-4 h-4 text-gray-600" />
                                </div>
                                <div>
                                  <div className="text-sm font-medium text-gray-900">
                                    {evenement.user.nom}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {evenement.user.email}
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            {evenement.beneficiaire ? (
                              <div className="flex items-center space-x-3">
                                <div className="bg-blue-50 p-2 rounded-lg">
                                  <HiOutlineOfficeBuilding className="w-4 h-4 text-blue-600" />
                                </div>
                                <div>
                                  <div className="text-sm font-medium text-gray-900">
                                    {evenement.beneficiaire.nom}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {evenement.beneficiaire.type}
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${statusConfig.bg} ${statusConfig.color} ${statusConfig.border}`}>
                              <span className={`w-2 h-2 rounded-full ${statusConfig.dot}`}></span>
                              {statusConfig.icon}
                              {statusConfig.label}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">
                              {formatDate(evenement.created_at)}
                            </div>
                            <div className="text-xs text-gray-500">
                              {getTimeAgo(evenement.created_at)}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => handleViewDetails(evenement)}
                                className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                title="Voir les détails"
                              >
                                <FiEye className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteClick(evenement)}
                                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Supprimer"
                              >
                                <FiTrash2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => toggleRowExpansion(evenement.id)}
                                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                                title="Afficher plus"
                              >
                                {isExpanded ? (
                                  <FiChevronDown className="w-4 h-4" />
                                ) : (
                                  <FiChevronRight className="w-4 h-4" />
                                )}
                              </button>
                            </div>
                          </td>
                        </tr>
                        
                        {/* Row Expansion */}
                        {isExpanded && (
                          <tr>
                            <td colSpan="6" className="px-6 py-4 ">
                              <div className="pl-14">
                                <div className="bg-white rounded-lg border border-gray-200 p-4">
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                      <h4 className="text-sm font-medium text-gray-700 mb-2">Transport</h4>
                                      <div className="flex items-center space-x-2">
                                        {evenement.transport ? (
                                          <>
                                            <FiTruckIcon className="w-4 h-4 text-green-600" />
                                            <span className="text-sm text-gray-900">Avec transport</span>
                                          </>
                                        ) : (
                                          <span className="text-sm text-gray-500">Sans transport</span>
                                        )}
                                      </div>
                                    </div>
                                    
                                    <div>
                                      <h4 className="text-sm font-medium text-gray-700 mb-2">Pack d'enterrement</h4>
                                      {evenement.packEnterrement ? (
                                        <div className="flex items-center space-x-2">
                                          <FiPackage className="w-4 h-4 text-blue-600" />
                                          <span className="text-sm text-gray-900">{evenement.packEnterrement.nom}</span>
                                        </div>
                                      ) : (
                                        <span className="text-sm text-gray-500">Aucun pack</span>
                                      )}
                                    </div>
                                    
                                    {evenement.motif_refus && (
                                      <div className="md:col-span-2">
                                        <h4 className="text-sm font-medium text-gray-700 mb-2">Motif de refus</h4>
                                        <div className="flex items-start space-x-2">
                                          <FiAlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                                          <p className="text-sm text-red-600">{evenement.motif_refus}</p>
                                        </div>
                                      </div>
                                    )}
                                    
                                    {evenement.materiels && evenement.materiels.length > 0 && (
                                      <div className="md:col-span-2">
                                        <h4 className="text-sm font-medium text-gray-700 mb-2">Matériels</h4>
                                        <div className="space-y-2">
                                          {evenement.materiels.slice(0, 3).map((materiel, index) => (
                                            <div key={materiel.id} className="flex justify-between items-center text-sm">
                                              <span className="text-gray-600">{materiel.nom}</span>
                                              <span className="font-medium text-gray-900">
                                                {materiel.pivot?.quantite_demandee || 0} unité(s)
                                              </span>
                                            </div>
                                          ))}
                                          {evenement.materiels.length > 3 && (
                                            <div className="text-xs text-gray-500 text-center">
                                              + {evenement.materiels.length - 3} autre(s) matériel(s)
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Dialog Détails */}
      {openDetailsDialog && selectedEvenement && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity 0 bg-opacity-75" aria-hidden="true"></div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="px-6 pt-6 pb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="bg-blue-50 p-3 rounded-lg">
                      <FiCalendar className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        Événement #{selectedEvenement.id}
                      </h3>
                      <p className="text-sm text-gray-500">
                        Créé {getTimeAgo(selectedEvenement.created_at)}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setOpenDetailsDialog(false)}
                    className="text-gray-400 hover:text-gray-500 transition-colors"
                  >
                    <FiX className="w-6 h-6" />
                  </button>
                </div>
              </div>
              
              <div className="px-6 py-4 border-t border-b border-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Informations Générales */}
                  <div className="space-y-4">
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-3">Informations Générales</h4>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-500">Type</span>
                          <div className="flex items-center space-x-2">
                            {getTypeConfig(selectedEvenement.type).icon}
                            <span className="text-sm font-medium text-gray-900">
                              {getTypeConfig(selectedEvenement.type).label}
                            </span>
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-500">Statut</span>
                          <div className="flex items-center space-x-2">
                            <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium ${getStatusConfig(selectedEvenement.statut).bg} ${getStatusConfig(selectedEvenement.statut).color}`}>
                              {getStatusConfig(selectedEvenement.statut).icon}
                              {getStatusConfig(selectedEvenement.statut).label}
                            </span>
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-500">Transport</span>
                          <div className="flex items-center space-x-2">
                            {selectedEvenement.transport ? (
                              <>
                                <FiTruckIcon className="w-4 h-4 text-green-600" />
                                <span className="text-sm font-medium text-gray-900">Avec transport</span>
                              </>
                            ) : (
                              <span className="text-sm text-gray-500">Sans transport</span>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-500">Date de création</span>
                          <span className="text-sm font-medium text-gray-900">
                            {formatDateTime(selectedEvenement.created_at)}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Pack d'enterrement */}
                    {selectedEvenement.packEnterrement && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-3">Pack d'enterrement</h4>
                        <div className="bg-blue-50 rounded-lg p-4">
                          <div className="flex items-start space-x-3">
                            <FiPackage className="w-5 h-5 text-blue-600 mt-0.5" />
                            <div>
                              <h5 className="text-sm font-medium text-gray-900">{selectedEvenement.packEnterrement.nom}</h5>
                              {selectedEvenement.packEnterrement.description && (
                                <p className="text-sm text-gray-600 mt-1">{selectedEvenement.packEnterrement.description}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Utilisateur et Bénéficiaire */}
                  <div className="space-y-4">
                    {/* Utilisateur */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-3">Utilisateur</h4>
                      {selectedEvenement.user ? (
                        <div className=" rounded-lg p-4">
                          <div className="flex items-start space-x-3">
                            <FiUser className="w-5 h-5 text-gray-600 mt-0.5" />
                            <div className="space-y-1">
                              <h5 className="text-sm font-medium text-gray-900">{selectedEvenement.user.name}</h5>
                              <div className="flex items-center space-x-2 text-sm text-gray-600">
                                <FiMail className="w-4 h-4" />
                                <span>{selectedEvenement.user.email}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-4 text-gray-500">Aucun utilisateur associé</div>
                      )}
                    </div>
                    
                    {/* Bénéficiaire */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-3">Bénéficiaire</h4>
                      {selectedEvenement.beneficiaire ? (
                        <div className="bg-blue-50 rounded-lg p-4">
                          <div className="flex items-start space-x-3">
                            <HiOutlineOfficeBuilding className="w-5 h-5 text-blue-600 mt-0.5" />
                            <div className="space-y-1">
                              <h5 className="text-sm font-medium text-gray-900">{selectedEvenement.beneficiaire.nom}</h5>
                              <div className="flex items-center space-x-2">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-white text-gray-800">
                                  {selectedEvenement.beneficiaire.type}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-4 text-gray-500">Aucun bénéficiaire associé</div>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Matériels */}
                {selectedEvenement.materiels && selectedEvenement.materiels.length > 0 && (
                  <div className="mt-6">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">
                      Matériels ({getTotalMateriels(selectedEvenement.materiels)})
                    </h4>
                    <div className=" rounded-lg overflow-hidden">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Matériel
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Demandé
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Validé
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Livré
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Reçu
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {selectedEvenement.materiels.map((materiel) => (
                            <tr key={materiel.id} className="hover:bg-white">
                              <td className="px-4 py-3">
                                <div className="text-sm font-medium text-gray-900">{materiel.nom}</div>
                              </td>
                              <td className="px-4 py-3">
                                <span className="text-sm text-gray-900">
                                  {materiel.pivot?.quantite_demandee || 0}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <span className={`text-sm ${materiel.pivot?.quantite_validee ? 'text-emerald-600 font-medium' : 'text-gray-500'}`}>
                                  {materiel.pivot?.quantite_validee || '-'}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <span className={`text-sm ${materiel.pivot?.quantite_livree ? 'text-blue-600 font-medium' : 'text-gray-500'}`}>
                                  {materiel.pivot?.quantite_livree || '-'}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <span className={`text-sm ${materiel.pivot?.quantite_recue ? 'text-green-600 font-medium' : 'text-gray-500'}`}>
                                  {materiel.pivot?.quantite_recue || '-'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                
                {/* Motif de refus */}
                {selectedEvenement.motif_refus && (
                  <div className="mt-6">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Motif de refus</h4>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-start space-x-3">
                        <FiAlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-red-700">{selectedEvenement.motif_refus}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="px-6 py-4  flex justify-end space-x-3">
                <button
                  onClick={() => setOpenDetailsDialog(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover: transition-colors"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Dialog Suppression */}
      {openDeleteDialog && selectedEvenement && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity 0 bg-opacity-75" aria-hidden="true"></div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="px-6 pt-6 pb-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-900">Confirmer la suppression</h3>
                  <button
                    onClick={() => setOpenDeleteDialog(false)}
                    className="text-gray-400 hover:text-gray-500 transition-colors"
                  >
                    <FiX className="w-6 h-6" />
                  </button>
                </div>
              </div>
              
              <div className="px-6 py-4 border-t border-b border-gray-200">
                <div className="flex items-start space-x-4">
                  <div className="bg-red-100 p-3 rounded-full flex-shrink-0">
                    <HiOutlineExclamationCircle className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <p className="text-gray-700">
                      Êtes-vous sûr de vouloir supprimer l'événement{' '}
                      <span className="font-semibold">#{selectedEvenement.id}</span> ?
                    </p>
                    <div className="mt-3 p-3  rounded-lg">
                      <p className="text-sm font-medium text-gray-900">{getTypeConfig(selectedEvenement.type).label}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        Utilisateur: {selectedEvenement.user?.name || 'Non spécifié'}
                      </p>
                    </div>
                    <p className="text-sm text-red-600 mt-3">
                      ⚠️ Cette action est irréversible.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="px-6 py-4  flex justify-end space-x-3">
                <button
                  onClick={() => setOpenDeleteDialog(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover: transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 bg-red-600 rounded-lg text-sm font-medium text-white hover:bg-red-700 transition-colors flex items-center gap-2"
                >
                  <FiTrash2 className="w-4 h-4" />
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Snackbar */}
      {snackbar.open && (
        <div className="fixed bottom-4 right-4 z-50 animate-slide-up">
          <div className={`rounded-lg shadow-lg p-4 max-w-md transform transition-all duration-300 ${
            snackbar.severity === 'success' 
              ? 'bg-emerald-50 border border-emerald-200' 
              : 'bg-red-50 border border-red-200'
          }`}>
            <div className="flex items-start">
              <div className={`flex-shrink-0 ${
                snackbar.severity === 'success' ? 'text-emerald-400' : 'text-red-400'
              }`}>
                {snackbar.severity === 'success' ? (
                  <FiCheckCircle className="w-5 h-5" />
                ) : (
                  <HiOutlineExclamationCircle className="w-5 h-5" />
                )}
              </div>
              <div className="ml-3 flex-1">
                <p className={`text-sm font-medium ${
                  snackbar.severity === 'success' ? 'text-emerald-800' : 'text-red-800'
                }`}>
                  {snackbar.message}
                </p>
              </div>
              <button
                onClick={() => setSnackbar(prev => ({ ...prev, open: false }))}
                className="ml-4 flex-shrink-0"
              >
                <FiX className="w-4 h-4 text-gray-400 hover:text-gray-600 transition-colors" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EvenementsPage;